import posthog from 'posthog-js'
import { ProductEventsApiError, type PageEventPayload } from '@/types/productEvents'
import { Sentry } from '@/lib/sentry'
import { describePage, type PageDescription } from '@/lib/pageEvents'
import { MASKED_AUTOCAPTURE, MEMORY_ONLY_ANALYTICS, analyticsProbe } from '@financialharmony/ui/sessionRecording'

type PlatformRole = 'platform_admin' | 'consultant' | 'client'

let initialised = false

/**
 * Identified product analytics for staff (consultants and platform admins), with their clicks and
 * a masked screen recording (FIN-1150, FIN-1532). Nothing is stored on the device — identity lives
 * in memory and is gone on reload or sign-out. Clients are never identified, captured or recorded
 * here. Group analytics is a paid
 * add-on we don't have, so the company is a plain event property, not a PostHog group. What this
 * sends is recorded in org/compliance/third-parties/posthog.md.
 */
export function initAnalytics(): boolean {
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key) return false

  // Recording and click capture start only once the signed-in user is known to be staff (identifyUser).
  posthog.init(key, {
    ...MEMORY_ONLY_ANALYTICS,
    loaded: (ph) => ph.register({ surface: 'consultant' }),
  })
  initialised = true
  // Spectest builds only: lets an end-to-end test read who PostHog thinks the user is, since its
  // requests never leave that environment. Production bundles never expose it.
  if (import.meta.env.VITE_ENVIRONMENT === 'spectest') window.__analyticsProbe = analyticsProbe(posthog)
  return true
}

export function identifyUser(userId: string, platformRole: PlatformRole | null): void {
  const staff = platformRole === 'consultant' || platformRole === 'platform_admin'
  safely(() => setStaff(staff))
  if (!initialised || !staff) return
  posthog.identify(userId, { is_staff: true, role: platformRole })
  withoutThrowing(() => {
    posthog.set_config({ autocapture: MASKED_AUTOCAPTURE, capture_dead_clicks: true })
    posthog.startSessionRecording(true)
  })
}

/** A recorder that fails to load or start (ad blocker, CSP) must never break sign-in or sign-out. */
function withoutThrowing(step: () => void): void {
  try {
    step()
  } catch (error) {
    Sentry.captureMessage('Consultant session recording failed to start or stop', {
      level: 'warning',
      tags: { feature: 'staff_session_recording' },
      extra: { reason: error instanceof Error ? `${error.name}: ${error.message}` : typeof error },
    })
  }
}

export function setCompany(companyId: string | null): void {
  safely(() => setPageCompany(companyId))
  if (!initialised) return
  if (companyId) posthog.register({ company_id: companyId })
  else posthog.unregister('company_id')
}

export function resetAnalytics(): void {
  safely(endPageSession)
  if (!initialised) return
  withoutThrowing(() => {
    posthog.stopSessionRecording()
    posthog.set_config({ autocapture: false, capture_dead_clicks: false })
  })
  posthog.reset()
}

// ── Page events into our own store (FIN-1154 PE-R14) ────────────────────────────────────────
// Every staff page view, and a page_left when the page closes (route change, company switch, tab
// hidden or closed), goes to product-events-api for time per company and per document (PE-R15).
// Independent of PostHog: it runs with no PostHog key. Held in memory only, never on the device:
// a page event that cannot be sent is lost, never stored for later. Sending never throws into
// navigation.

const FLUSH_DELAY_MS = 5_000
const MAX_BATCH = 50
const MAX_QUEUE = 200
const MAX_ATTEMPTS = 3
/** Sign-out waits this long at most for the last page events; a stalled network never blocks it. */
const SIGN_OUT_FLUSH_LIMIT_MS = 2_000

interface OpenPage extends PageDescription {
  /** The company the open page_viewed was sent for; null while the page is not open. */
  openFor: string | null
}

interface Queued {
  event: PageEventPayload
  attempts: number
}

let staffUser = false
let pageCompanyId: string | null = null
let sessionId: string | null = null
let currentPage: OpenPage | null = null
let queue: Queued[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let inFlight = false
let failureReported = false
let listening = false
let serviceRegistry: Promise<typeof import('@/services/ServiceRegistry')> | null = null

/**
 * Starts closing the open page when the tab is hidden or closed. Called once from main.tsx;
 * returns a function that removes the listeners again (for tests).
 */
export function initPageEvents(): () => void {
  if (listening) return () => {}
  listening = true
  // Loaded now, so a send while the tab is closing does not first wait for a code chunk.
  void loadServiceRegistry().catch(() => {})
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      safely(closeCurrentPage)
      void flush(true)
    } else {
      safely(openCurrentPage)
    }
  }
  const onPageHide = () => {
    safely(closeCurrentPage)
    void flush(true)
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageHide)
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pagehide', onPageHide)
    listening = false
  }
}

/** Called on every location change (App.tsx's PageEventsTracker). */
export function recordPageView(pathname: string, search: string): void {
  safely(() => {
    const page = describePage(pathname, search)
    if (
      page && currentPage && page.route === currentPage.route && page.entityId === currentPage.entityId
    ) {
      return
    }
    closeCurrentPage()
    currentPage = page ? { ...page, openFor: null } : null
    openCurrentPage()
  })
}

/**
 * Closes the open page and sends everything queued, while the session still exists. Awaited
 * before sign-out, for at most SIGN_OUT_FLUSH_LIMIT_MS.
 */
export async function flushAnalytics(): Promise<void> {
  safely(closeCurrentPage)
  let limit: ReturnType<typeof setTimeout> | undefined
  await Promise.race([
    flush(true),
    new Promise<void>((resolve) => {
      limit = setTimeout(resolve, SIGN_OUT_FLUSH_LIMIT_MS)
    }),
  ])
  clearTimeout(limit)
}

/** Page events never throw into navigation or sign-in: a failure is reported once and ignored. */
function safely(step: () => void): void {
  try {
    step()
  } catch (error) {
    reportOnce('Consultant page events failed in the browser', error, 0)
  }
}

function loadServiceRegistry() {
  // Loaded lazily, so importing analytics (main.tsx, AuthContext) never loads the Supabase client
  // and every service with it on its own.
  serviceRegistry ??= import('@/services/ServiceRegistry')
  return serviceRegistry
}

function setStaff(staff: boolean): void {
  if (staff === staffUser) return
  if (!staff) closeCurrentPage()
  staffUser = staff
  if (staff) openCurrentPage()
}

function setPageCompany(companyId: string | null): void {
  if (companyId === pageCompanyId) return
  closeCurrentPage()
  pageCompanyId = companyId
  openCurrentPage()
}

function endPageSession(): void {
  closeCurrentPage()
  void flush(true)
  staffUser = false
  pageCompanyId = null
  sessionId = null
}

function openCurrentPage(): void {
  if (!currentPage || currentPage.openFor || !staffUser || !pageCompanyId) return
  if (document.visibilityState === 'hidden') return
  currentPage.openFor = pageCompanyId
  enqueue('page_viewed', currentPage, pageCompanyId)
}

function closeCurrentPage(): void {
  if (!currentPage?.openFor) return
  enqueue('page_left', currentPage, currentPage.openFor)
  currentPage.openFor = null
}

function enqueue(name: PageEventPayload['name'], page: PageDescription, companyId: string): void {
  sessionId ??= crypto.randomUUID()
  queue.push({
    attempts: 0,
    event: {
      id: crypto.randomUUID(),
      name,
      occurredAt: new Date().toISOString(),
      route: page.route,
      area: page.area,
      entityType: page.entityType,
      entityId: page.entityId,
      companyId,
      sessionId,
    },
  })
  if (queue.length > MAX_QUEUE) queue = queue.slice(queue.length - MAX_QUEUE)
  if (queue.length >= MAX_BATCH) void flush(false)
  else scheduleFlush()
}

function scheduleFlush(): void {
  if (flushTimer || queue.length === 0) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flush(false)
  }, FLUSH_DELAY_MS)
}

/**
 * Sends the queue in batches of MAX_BATCH. `keepalive` (tab hidden or closing) sends at once,
 * alongside any batch already in flight, so the request can outlive the page.
 */
async function flush(keepalive: boolean): Promise<void> {
  if (keepalive) {
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    // The page may be going away: every batch is sent at once, none waits on another's response.
    const batches: Queued[][] = []
    while (queue.length > 0) batches.push(queue.splice(0, MAX_BATCH))
    await Promise.all(batches.map((batch) => sendBatch(batch, true)))
    scheduleFlush()
    return
  }
  if (inFlight) return
  inFlight = true
  try {
    while (queue.length > 0) {
      const batch = queue.splice(0, MAX_BATCH)
      const sent = await sendBatch(batch, keepalive)
      if (!sent) break
    }
  } finally {
    inFlight = false
    scheduleFlush()
  }
}

async function sendBatch(batch: Queued[], keepalive: boolean): Promise<boolean> {
  try {
    const { ServiceRegistry } = await loadServiceRegistry()
    await ServiceRegistry.getProductEventsService().sendPageEvents(batch.map((q) => q.event), { keepalive })
    return true
  } catch (error) {
    const retryable = !(error instanceof ProductEventsApiError) || error.retryable
    const retry = retryable ? batch.filter((q) => ++q.attempts < MAX_ATTEMPTS) : []
    queue = [...retry, ...queue]
    if (retry.length < batch.length) {
      const expired = error instanceof ProductEventsApiError && error.status === 401
      if (!expired) reportOnce('Consultant page events could not be stored', error, batch.length - retry.length)
    }
    return false
  }
}

function reportOnce(message: string, error: unknown, dropped: number): void {
  // Once per page load: an endpoint that is down would otherwise report every few seconds.
  if (failureReported) return
  failureReported = true
  Sentry.captureMessage(message, {
    level: 'warning',
    tags: { feature: 'product_page_events' },
    extra: {
      dropped,
      status: error instanceof ProductEventsApiError ? error.status : null,
      reason: error instanceof Error ? `${error.name}: ${error.message}` : typeof error,
    },
  })
}
